import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaArrowRight, FaChevronDown, FaUser, FaPhoneAlt, FaCheckCircle, FaWhatsapp, FaMinus, FaPlus } from 'react-icons/fa';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import DeliveryMap from '../components/DeliveryMap';
import PlaceAutocompleteInput from '../components/PlaceAutocompleteInput';
import { trackPurchase, trackInitiateCheckout } from '../utils/meta-pixel';
import { getAttribution, getQuoteSessionId, getQuotePrefill, saveQuotePrefill, resetQuoteSession, isQuoteLot2On, isQuoteLot6On, isQuoteOtpOn, saveOtpToken } from '../utils/attribution';
import { logQuoteLead } from '../utils/quote-lead';
import { normalizePhone, isValidPhone } from '../utils/phone';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

// Créée la commande sur le business service (REACT_APP_BUSINESS_API_ENDPOINT)
const ORDER_ENDPOINT_PATH = '/v1/public/order/quick';
// Pas d'ajustement du prix proposé par le client (mode inDrive), en FCFA.
const PRICE_STEP = 100;

const PHONE_PREFIX_BY_COUNTRY = { Benin: '+229', Togo: '+228' };

// Zone de couverture : au-delà de ce tarif (~30 km), la zone est considérée non desservie.
const COVERAGE_MAX_TARIFF = 3000;

// Villes desservies actuellement.
const CITY_OPTIONS = ['Cotonou', 'Calavi'];

const emptyPoint = { value: '', place: null, contactName: '', phone: '', city: '' };

// Build a point from data passed by another page (Hero, GetQuote). Contact fields
// are carried over when provided (the Hero form now collects them).
const pointFromPrefill = (p) =>
  p ? { value: p.value || '', place: p.place || null, contactName: p.contactName || '', phone: p.phone || '', city: p.city || '' } : emptyPoint;

// Lot 3 — cache local des numéros déjà vérifiés (le serveur reste la source de vérité).
const VERIFIED_PHONES_KEY = 'ogg_verified_phones';
const isPhoneVerifiedLocally = (p) => {
  try {
    return p ? JSON.parse(localStorage.getItem(VERIFIED_PHONES_KEY) || '[]').includes(p) : false;
  } catch (_) {
    return false;
  }
};
const rememberVerifiedPhone = (p) => {
  if (!p) return;
  try {
    const arr = JSON.parse(localStorage.getItem(VERIFIED_PHONES_KEY) || '[]');
    if (!arr.includes(p)) {
      arr.push(p);
      localStorage.setItem(VERIFIED_PHONES_KEY, JSON.stringify(arr));
    }
  } catch (_) {
    // stockage indisponible : le serveur revérifiera
  }
};

// Petit loader affiché dans les boutons pendant une action (au lieu d'un texte « … en cours… »).
const Spinner = () => (
  <span className="inline-block w-5 h-5 rounded-full border-2 border-white/40 border-t-white animate-spin" aria-hidden="true" />
);

// Main QuickDelivery content (rendered inside Wrapper)
const QuickDeliveryContent = () => {
  const location = useLocation();
  const prefill = location.state || null;
  // Préremplissage du même appareil (Lot 1) : point d'enlèvement habituel + contact expéditeur.
  // La navigation (prefill) prime sur les dernières valeurs stockées localement.
  const devicePrefill = getQuotePrefill();

  const deliveryType = 'Local'; // International retiré de la vitrine (livraison locale uniquement)
  // Un seul pays desservi actuellement : Bénin (plus de sélecteur).
  const [country] = useState('Benin');
  const [pickup, setPickup] = useState(pointFromPrefill(prefill?.pickup || devicePrefill?.pickup));
  // Lot 6 — qui commande : 'sender' (j'envoie), 'buyer' (j'achète/je reçois), 'merchant' (vendeur).
  // Détermine quel numéro l'OTP vérifie. La navigation (prefill) prime sur les valeurs locales.
  const rawSenderType = prefill?.senderType || devicePrefill?.senderType;
  const [senderType, setSenderType] = useState(rawSenderType === 'self' ? 'sender' : (rawSenderType || 'sender'));
  const [merchant, setMerchant] = useState(prefill?.merchant || devicePrefill?.merchant || { name: '', phone: '' });
  const [dropoff, setDropoff] = useState(pointFromPrefill(prefill?.dropoff));
  const [simulationResult, setSimulationResult] = useState(null);
  // Prix proposé par le client : pré-rempli avec le tarif plateforme, modifiable (plancher = minPrice).
  const [proposedPrice, setProposedPrice] = useState('');
  const [confirmedTrackingId, setConfirmedTrackingId] = useState('');
  const [fallbackSent, setFallbackSent] = useState(false); // Lot 2 : demande envoyée sans point exact
  // Lot 3 — OTP
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpPhone, setOtpPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showMap, setShowMap] = useState(false);
  const mobileResultRef = useRef(null);
  const didMountRef = useRef(false);

  const phonePrefix = PHONE_PREFIX_BY_COUNTRY[country] || '+229';
  // Hors zone de couverture : tarif estimé au-delà du plafond (~30 km). On bloque la commande.
  const outOfCoverage = !!simulationResult && Number(simulationResult.grandTotalTariff) > COVERAGE_MAX_TARIFF;
  const inputsDisabled = deliveryType !== 'Local' || !country;

  const resetResult = () => {
    setSimulationResult(null);
    setShowMap(false);
    setError('');
  };

  // Arriving on this page (e.g. from the simulation page) should start at the top,
  // React Router does not reset scroll on navigation.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Reset places when country changes — but not on first mount, so any addresses
  // pre-filled from the simulation page are preserved.
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setPickup(emptyPoint);
    setDropoff(emptyPoint);
    setSimulationResult(null);
    setShowMap(false);
    setError('');
    setFieldErrors({});
  }, [country]);

  const formatDuration = (minutesInput) => {
    if (minutesInput === undefined || minutesInput === null) return '';
    const minutes = Math.round(minutesInput);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h${mins > 0 ? ` ${mins} min` : ''}`;
  };

  const formatDistance = (kmInput) => {
    if (kmInput === undefined || kmInput === null) return '-';
    const roundedUp = Math.ceil(kmInput * 10) / 10;
    return `${roundedUp.toFixed(1)} km`;
  };

  const updatePoint = (setter, patch, errorKeys = []) => {
    resetResult();
    setter((prev) => ({ ...prev, ...patch }));
    if (errorKeys.length > 0) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        errorKeys.forEach((key) => { delete next[key]; });
        return next;
      });
    }
  };

  const sanitizePhone = (raw) => raw.replace(/[^\d\s]/g, '');

  const clearFieldError = (key) =>
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

  // requirePhones : Lot 2 -> les deux téléphones (valides) sont exigés AVANT l'estimation.
  // requireContacts : en plus, les noms, exigés au moment de confirmer la course.
  const validateForm = ({ requireContacts = true, requirePhones = false } = {}) => {
    const errors = {};
    if (!CITY_OPTIONS.includes(pickup.city)) {
      errors.pickupCity = 'Choisissez la ville de récupération.';
    }
    if (!CITY_OPTIONS.includes(dropoff.city)) {
      errors.dropoffCity = 'Choisissez la ville de livraison.';
    }
    if (!pickup.place?.lat || !pickup.place?.lng) {
      errors.pickupPlace = 'Choisissez le lieu de ramassage dans les suggestions ou sur la carte.';
    }
    if (!dropoff.place?.lat || !dropoff.place?.lng) {
      errors.dropoffPlace = 'Choisissez le lieu de livraison dans les suggestions ou sur la carte.';
    }
    if (requireContacts || requirePhones) {
      if (!isValidPhone(pickup.phone, country)) {
        errors.pickupPhone = 'Numéro invalide. Ex: 0196123456';
      }
      if (!isValidPhone(dropoff.phone, country)) {
        errors.dropoffPhone = 'Numéro invalide. Ex: 0196123456';
      }
      // Lot 6 — vendeur : son propre numéro requis et distinct du fournisseur / destinataire.
      if (isQuoteLot6On() && senderType === 'merchant') {
        if (!merchant.name.trim()) {
          errors.merchantName = 'Veuillez saisir votre nom (vendeur).';
        }
        if (!isValidPhone(merchant.phone, country)) {
          errors.merchantPhone = 'Numéro vendeur invalide. Ex: 0196123456';
        } else {
          const m = normalizePhone(merchant.phone, country);
          if (m && (m === normalizePhone(pickup.phone, country) || m === normalizePhone(dropoff.phone, country))) {
            errors.merchantPhone = 'Votre numéro doit être différent de ceux du fournisseur et du destinataire.';
          }
        }
      }
    }
    if (requireContacts) {
      if (!pickup.contactName.trim()) {
        errors.pickupName = 'Veuillez saisir le nom de la personne au départ.';
      }
      if (!dropoff.contactName.trim()) {
        errors.dropoffName = 'Veuillez saisir le nom du destinataire.';
      }
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Appel de simulation tarifaire (ne nécessite que les coordonnées).
  const runSimulation = async () => {
    setError('');
    setSimulationResult(null);
    setShowMap(false);

    const payload = {
      deliveryPoints: [
        {
          referenceId: dropoff.place?.name || dropoff.place?.address || dropoff.value,
          plat: pickup.place.lat,
          plng: pickup.place.lng,
          dlat: dropoff.place.lat,
          dlng: dropoff.place.lng,
        },
      ],
      rideTypeId: 1,
      productTypeId: 1,
    };

    try {
      setLoading(true);
      const apiBase = process.env.REACT_APP_API_ENDPOINT;
      const res = await fetch(`${apiBase}/v1/public/tariff/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`Erreur ${res.status}`);
      }

      const data = await res.json();
      setSimulationResult(data);
      // Prix proposé par le client : pré-rempli avec le tarif plateforme, éditable (plancher = minPrice).
      setProposedPrice(data?.grandTotalTariff != null ? String(data.grandTotalTariff) : '');
      setShowMap(true);
      // Étape intermédiaire du tunnel : devis obtenu.
      trackInitiateCheckout({ value: data.grandTotalTariff });
      // Capture du lead + étape 'estimated' (donnée fraîche, l'état n'est pas encore à jour).
      lastStepRef.current = 'estimated';
      captureLead(data, 'estimated');

      // Auto-scroll to mobile result on small screens
      setTimeout(() => {
        if (mobileResultRef.current && window.innerWidth < 1024) {
          mobileResultRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 100);
    } catch (err) {
      setError(err.message || 'Erreur lors de la validation');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = (e) => {
    e.preventDefault();
    setError('');
    if (!country) {
      setError('Merci de sélectionner un pays avant de renseigner les points.');
      return;
    }
    // Lot 2 : téléphones (valides) requis avant d'afficher le tarif.
    if (!validateForm({ requireContacts: false, requirePhones: isQuoteLot2On() })) return;
    runSimulation();
  };

  // À l'arrivée depuis une autre page (index) avec des adresses complètes,
  // on lance l'estimation automatiquement — l'utilisateur n'a pas à re-cliquer.
  const autoRanRef = useRef(false);
  useEffect(() => {
    if (autoRanRef.current) return;
    const prefilledPlaces = prefill?.pickup?.place?.lat && prefill?.dropoff?.place?.lat;
    if (prefilledPlaces && country && pickup.place?.lat && pickup.place?.lng && dropoff.place?.lat && dropoff.place?.lng) {
      autoRanRef.current = true;
      runSimulation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, pickup.place, dropoff.place]);

  // Capture progressive du lead de devis (fire-and-forget) : trajet + tarif + contacts si présents.
  const captureLead = (sim, lastStep, extra) => {
    const s = sim || simulationResult;
    // Numéros normalisés en E.164 (dédup / relance / OTP).
    const senderPhone = normalizePhone(pickup.phone, country) || '';
    const recipientPhone = normalizePhone(dropoff.phone, country) || '';
    logQuoteLead({
      // Expéditeur (enlèvement) ; on garde un fallback destinataire pour la relance si vide.
      name: pickup.contactName || dropoff.contactName || '',
      phone: senderPhone || recipientPhone,
      // Destinataire (livraison).
      delivery_name: dropoff.contactName || '',
      delivery_phone: recipientPhone,
      pickup_address: pickup.place?.address || pickup.value || '',
      pickup_lat: pickup.place?.lat || 0,
      pickup_lng: pickup.place?.lng || 0,
      pickup_city: pickup.city || '',
      delivery_address: dropoff.place?.address || dropoff.value || '',
      delivery_lat: dropoff.place?.lat || 0,
      delivery_lng: dropoff.place?.lng || 0,
      delivery_city: dropoff.city || '',
      tariff: s?.grandTotalTariff || 0,
      distance: s?.grandTotalDistance || 0,
      ...(isQuoteLot2On() && lastStep ? { last_step: lastStep } : {}),
      ...(isQuoteLot6On() ? {
        sender_type: senderType,
        merchant_name: senderType === 'merchant' ? merchant.name : '',
        merchant_phone: senderType === 'merchant' ? (normalizePhone(merchant.phone, country) || '') : '',
      } : {}),
      ...(extra || {}),
    });
  };

  // Lot 2 — repli : l'utilisateur n'arrive pas à placer ses points. On envoie la demande avec
  // « tarif à confirmer » (needs_confirmation) ; le SAV la voit et rappelle. Un numéro suffit.
  const handleFallback = () => {
    if (!isValidPhone(pickup.phone, country) && !isValidPhone(dropoff.phone, country)) {
      setFieldErrors((prev) => ({ ...prev, pickupPhone: 'Indiquez au moins un numéro pour être rappelé.' }));
      return;
    }
    captureLead(null, 'fallback_no_point', { needs_confirmation: true });
    setFallbackSent(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Lot 2 : suivi de la progression (étape la plus avancée atteinte) pour diagnostiquer les abandons.
  const STEP_RANK = { pickup_selected: 1, delivery_selected: 2, estimated: 3 };
  const lastStepRef = useRef('');
  const trackStep = (step) => {
    if (!isQuoteLot2On()) return;
    if ((STEP_RANK[step] || 0) <= (STEP_RANK[lastStepRef.current] || 0)) return;
    lastStepRef.current = step;
    captureLead(null, step);
  };

  // Capture débouncée à la saisie du téléphone : fiable sur mobile où le blur ne se déclenche pas
  // toujours (l'utilisateur tape puis quitte sans "sortir" du champ). Réenrichit le même lead.
  const leadTimerRef = useRef(null);
  const scheduleLeadCapture = () => {
    if (leadTimerRef.current) clearTimeout(leadTimerRef.current);
    leadTimerRef.current = setTimeout(() => { captureLead(); }, 900);
  };

  // Numéro de celui qui commande (vendeur si vendeur, sinon expéditeur) — c'est lui qu'on vérifie.
  const ordererPhoneE164 = () => {
    if (senderType === 'merchant') return normalizePhone(merchant.phone, country); // son propre n°
    if (senderType === 'buyer') return normalizePhone(dropoff.phone, country);      // il reçoit -> livraison
    return normalizePhone(pickup.phone, country);                                   // il envoie -> récupération
  };

  const handleConfirm = async () => {
    if (outOfCoverage) return; // zone non couverte : on ne crée pas la commande
    setError('');

    // Les contacts (nom + téléphone) sont exigés à cette étape uniquement.
    if (!validateForm({ requireContacts: true })) return;

    // Prix proposé : garde-fou client (le serveur reste l'autorité via minPrice).
    const price = Number(proposedPrice);
    if (proposedPrice === '' || !Number.isFinite(price) || price <= 0) {
      setError('Merci d\'indiquer un prix valide.');
      return;
    }
    const floor = simulationResult?.minPrice;
    if (floor != null && price < floor) {
      setError(`Le prix minimum est de ${floor} FCFA.`);
      return;
    }

    // Lot 3 — porte OTP : on vérifie le numéro de celui qui commande avant de créer la commande.
    if (isQuoteOtpOn()) {
      const phone = ordererPhoneE164();
      if (phone && !isPhoneVerifiedLocally(phone)) {
        setOtpPhone(phone);
        setOtpCode('');
        setOtpError('');
        const r = await sendOtp(phone);
        if (r.verified) {
          rememberVerifiedPhone(phone);
        } else {
          setOtpOpen(true);
          if (!r.ok) setOtpError(r.message || 'Envoi du code impossible.');
          return;
        }
      }
    }

    submitOrder();
  };

  // Envoi du code OTP. Renvoie {verified, ok, message}.
  const sendOtp = async (phone) => {
    setOtpSending(true);
    try {
      const apiBase = process.env.REACT_APP_BUSINESS_API_ENDPOINT;
      const res = await fetch(`${apiBase}/v1/public/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const d = await res.json().catch(() => null);
      return { verified: !!(d && d.verified), ok: res.ok, message: d && d.message };
    } catch (_) {
      return { verified: false, ok: false, message: 'Erreur réseau. Réessayez.' };
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtp = async () => {
    if (!/^[0-9]{4}$/.test(otpCode.trim())) {
      setOtpError('Entrez le code à 4 chiffres reçu par WhatsApp.');
      return;
    }
    setOtpVerifying(true);
    setOtpError('');
    try {
      const apiBase = process.env.REACT_APP_BUSINESS_API_ENDPOINT;
      const res = await fetch(`${apiBase}/v1/public/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: otpPhone, code: otpCode.trim() }),
      });
      const d = await res.json().catch(() => null);
      if (d && d.verified) {
        rememberVerifiedPhone(otpPhone);
        if (d.token) saveOtpToken(d.phone || otpPhone, d.token); // débloque "Mes expéditions" par numéro
        setOtpOpen(false);
        submitOrder();
        return;
      }
      const reason = d && d.reason;
      if (reason === 'expired') setOtpError('Code expiré. Demandez-en un nouveau.');
      else if (reason === 'locked') setOtpError('Trop d\'essais. Demandez un nouveau code.');
      else setOtpError(`Code incorrect${d && typeof d.remaining === 'number' ? ` (${d.remaining} essai(s) restant(s))` : ''}.`);
    } catch (_) {
      setOtpError('Erreur réseau. Réessayez.');
    } finally {
      setOtpVerifying(false);
    }
  };

  const resendOtp = async () => {
    setOtpError('');
    const r = await sendOtp(otpPhone);
    if (r.verified) {
      rememberVerifiedPhone(otpPhone);
      setOtpOpen(false);
      submitOrder();
      return;
    }
    if (!r.ok) setOtpError(r.message || 'Renvoi impossible.');
  };

  const submitOrder = async () => {
    // Pickup = sender, delivery ("customer") = recipient. Matches OrderController.createQuickOrder.
    const payload = {
      customer_name: dropoff.contactName.trim(),
      customer_phone: normalizePhone(dropoff.phone, country) || (phonePrefix + dropoff.phone.replace(/\s+/g, '')),
      delivery_address: dropoff.place.address || dropoff.value,
      delivery_lat: dropoff.place.lat,
      delivery_lng: dropoff.place.lng,
      delivery_city: dropoff.city,
      pickup_contact_name: pickup.contactName.trim(),
      pickup_contact_phone: normalizePhone(pickup.phone, country) || (phonePrefix + pickup.phone.replace(/\s+/g, '')),
      pickup_address: pickup.place.address || pickup.value,
      pickup_lat: pickup.place.lat,
      pickup_lng: pickup.place.lng,
      pickup_city: pickup.city,
      // Signed price token from the preview: lets the backend lock the previewed
      // price onto the order (verified server-side, tamper-proof).
      price_token: simulationResult?.priceToken,
      // Prix proposé par le client (peut différer du tarif plateforme). Le backend valide
      // le plancher (minPrice) et marque la source du tarif (MERCHANT si modifié, sinon PLATFORM).
      ...(proposedPrice !== '' && !Number.isNaN(Number(proposedPrice))
        ? { proposed_tariff: Number(proposedPrice) }
        : {}),
      ...(isQuoteLot6On() ? {
        sender_type: senderType,
        merchant_name: senderType === 'merchant' ? merchant.name.trim() : '',
        merchant_phone: senderType === 'merchant' ? (normalizePhone(merchant.phone, country) || '') : '',
      } : {}),
      attribution: { ...getAttribution(), session_id: getQuoteSessionId() },
    };

    try {
      setConfirming(true);
      const apiBase = process.env.REACT_APP_BUSINESS_API_ENDPOINT;
      const res = await fetch(`${apiBase}${ORDER_ENDPOINT_PATH}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        // Erreurs métier du prix proposé : messages lisibles côté client.
        let serverMessage = '';
        try {
          const errBody = await res.json();
          serverMessage = errBody?.message || '';
        } catch (_) { /* corps non-JSON */ }
        if (serverMessage.startsWith('PROPOSED_TARIFF_BELOW_MIN')) {
          const min = serverMessage.match(/\d+/);
          throw new Error(min ? `Le prix minimum est de ${min[0]} FCFA.` : 'Prix en dessous du minimum autorisé.');
        }
        if (serverMessage.startsWith('PROPOSED_TARIFF_INVALID')) {
          throw new Error('Le prix proposé est invalide.');
        }
        throw new Error(`Erreur ${res.status}`);
      }

      const data = await res.json();
      const trackingId = data.trackingID || data.trackingId;
      if (!trackingId) {
        throw new Error('Numéro de suivi manquant dans la réponse.');
      }
      setConfirmedTrackingId(trackingId);
      // Conversion : commande de livraison passée sur la vitrine (prix réellement proposé).
      trackPurchase({ value: Number(proposedPrice) || simulationResult?.grandTotalTariff, orderId: trackingId });
      // Lot 1 : mémoriser l'enlèvement habituel pour le prochain devis, puis tourner le session_id
      // (la ligne devient convertie ; une nouvelle estimation créera une nouvelle ligne).
      saveQuotePrefill({
        country,
        pickup: { value: pickup.value, place: pickup.place, contactName: pickup.contactName, phone: pickup.phone, city: pickup.city },
        senderType,
        merchant: senderType === 'merchant' ? { name: merchant.name, phone: merchant.phone } : { name: '', phone: '' },
      });
      resetQuoteSession();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Erreur lors de la création de la commande');
    } finally {
      setConfirming(false);
    }
  };

  const inputClassName = "w-full bg-gray-100 rounded-lg py-3.5 pl-12 pr-10 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black transition-all";
  const textInputClassName = "w-full bg-gray-100 rounded-lg py-3 pl-10 pr-4 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black transition-all disabled:cursor-not-allowed disabled:opacity-60";

  // --- Prix proposé par le client (mode inDrive) ---
  const priceFloor = simulationResult?.minPrice ?? 0;
  // Le backend autorise la négociation seulement au-dessus d'un seuil (défaut 1000 F).
  // Fallback : si le champ n'est pas renvoyé, on s'aligne sur le seuil par défaut.
  const canProposePrice = simulationResult
    ? (simulationResult.allowProposedPrice ?? ((simulationResult.grandTotalTariff ?? 0) >= 1000))
    : false;

  const bumpPrice = (delta) => {
    const current = Number(proposedPrice) || simulationResult?.grandTotalTariff || 0;
    const next = Math.max(priceFloor, Math.round(current + delta));
    setProposedPrice(String(next));
  };
  const clampPriceOnBlur = () => {
    const n = Number(proposedPrice);
    if (!Number.isFinite(n) || n < priceFloor) setProposedPrice(String(priceFloor));
    else setProposedPrice(String(Math.round(n)));
  };

  // Contrôle de prix style inDrive : − [prix] +. `big` = variante desktop (plus grande).
  const renderPriceControl = (big) => {
    if (!canProposePrice) {
      // Sous le seuil : prix fixe, non négociable.
      return (
        <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Tarif total</p>
          <p className={`${big ? 'text-lg' : 'text-base'} font-bold text-gray-900`}>
            {simulationResult?.grandTotalTariff ?? '-'} FCFA
          </p>
        </div>
      );
    }
    const btn = `flex items-center justify-center rounded-full bg-orange-100 text-orange-600 hover:bg-orange-200 active:scale-95 transition ${big ? 'w-9 h-9' : 'w-8 h-8'}`;
    return (
      <div className="col-span-3 p-3 bg-orange-50 rounded-xl border border-orange-200">
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs uppercase tracking-wide text-gray-500">Proposez votre prix</p>
          <span className="text-[10px] font-medium text-orange-600">Modifiable</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <button type="button" aria-label="Baisser le prix" onClick={() => bumpPrice(-PRICE_STEP)} className={btn}>
            <FaMinus className={big ? 'w-3.5 h-3.5' : 'w-3 h-3'} />
          </button>
          <div className="flex-1 text-center">
            <div className="flex items-baseline justify-center gap-1">
              <input
                type="number"
                inputMode="numeric"
                value={proposedPrice}
                min={priceFloor}
                onChange={(e) => setProposedPrice(e.target.value)}
                onBlur={clampPriceOnBlur}
                className={`${big ? 'text-2xl w-24' : 'text-xl w-20'} font-extrabold text-gray-900 bg-transparent text-center focus:outline-none`}
              />
              <span className={`${big ? 'text-sm' : 'text-xs'} font-semibold text-gray-500`}>FCFA</span>
            </div>
            {priceFloor > 0 && (
              <p className="text-[10px] text-gray-400">Min : {priceFloor} FCFA</p>
            )}
          </div>
          <button type="button" aria-label="Augmenter le prix" onClick={() => bumpPrice(PRICE_STEP)} className={btn}>
            <FaPlus className={big ? 'w-3.5 h-3.5' : 'w-3 h-3'} />
          </button>
        </div>
        <p className="mt-2 text-center text-[11px] font-medium text-amber-600">
          ⚠️ Plus votre prix est bas, plus la prise en charge prendra du temps.
        </p>
      </div>
    );
  };

  // Sélecteur de ville (Cotonou / Calavi) — zones desservies.
  const renderCitySelect = (point, setter, errKey, placeholder) => (
    <div>
      <div className="relative">
        <select
          value={point.city}
          disabled={inputsDisabled}
          onChange={(e) => updatePoint(setter, { city: e.target.value }, [errKey])}
          className="w-full bg-gray-100 rounded-lg px-4 pr-10 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-black appearance-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
        >
          <option value="" disabled>{placeholder}</option>
          {CITY_OPTIONS.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
          <FaChevronDown className="w-3 h-3" />
        </div>
      </div>
      {fieldErrors[errKey] && <p className="mt-1 text-sm text-red-600">{fieldErrors[errKey]}</p>}
    </div>
  );

  const renderContactFields = (point, setter, keys, namePlaceholder) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <div>
        <div className="relative">
          <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={point.contactName}
            disabled={inputsDisabled}
            onChange={(e) => updatePoint(setter, { contactName: e.target.value }, [keys.name])}
            placeholder={namePlaceholder}
            className={textInputClassName}
          />
        </div>
        {fieldErrors[keys.name] && (
          <p className="mt-1 text-sm text-red-600">{fieldErrors[keys.name]}</p>
        )}
      </div>
      <div>
        <div className="relative">
          <FaPhoneAlt className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <span className="absolute left-11 top-1/2 -translate-y-1/2 text-sm text-gray-500 font-medium pointer-events-none">{phonePrefix}</span>
          <input
            type="tel"
            value={point.phone}
            disabled={inputsDisabled}
            onChange={(e) => { updatePoint(setter, { phone: sanitizePhone(e.target.value) }, [keys.phone]); scheduleLeadCapture(); }}
            onBlur={() => { if (point.phone) captureLead(); }}
            placeholder="Téléphone"
            className={`${textInputClassName} !pl-[5.25rem]`}
          />
        </div>
        {fieldErrors[keys.phone] && (
          <p className="mt-1 text-sm text-red-600">{fieldErrors[keys.phone]}</p>
        )}
      </div>
    </div>
  );

  if (fallbackSent) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center space-y-6">
          <div className="flex justify-center">
            <FaCheckCircle className="w-16 h-16 text-green-500" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Demande envoyée !</h1>
            <p className="text-gray-600">
              Nous avons bien reçu votre demande. Notre équipe vous rappelle très vite pour confirmer
              les points de récupération et de livraison et le tarif.
            </p>
          </div>
          <a
            href="https://wa.me/22945387945?text=Bonjour%2C%20j'ai%20demand%C3%A9%20une%20livraison%20sans%20pouvoir%20placer%20mes%20points."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 text-sm font-medium text-green-600 hover:text-green-700"
          >
            <FaWhatsapp className="w-4 h-4" /> Nous écrire sur WhatsApp (+229 45387945)
          </a>
        </div>
      </div>
    );
  }

  if (confirmedTrackingId) {
    const trackingPath = `/tracking?tracking-id=${encodeURIComponent(confirmedTrackingId)}&submit=1`;
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center space-y-6">
          <div className="flex justify-center">
            <FaCheckCircle className="w-16 h-16 text-green-500" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Demande confirmée !</h1>
            <p className="text-gray-600">
              Nous avons bien reçu votre demande de livraison. Notre équipe la prend en charge et vous recontacte au besoin.
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Numéro de suivi</p>
            <p className="text-lg font-bold text-gray-900 select-all">{confirmedTrackingId}</p>
          </div>
          <Link
            to={trackingPath}
            className="w-full px-8 py-3.5 rounded-lg font-medium bg-black text-white hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
          >
            Suivre ma commande <FaArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-xs text-gray-400">
            Conservez ce numéro : il vous permettra de suivre votre colis une fois la livraison lancée.
          </p>
          {isQuoteLot6On() && (
            <Link to="/mes-expeditions" className="inline-flex items-center justify-center gap-2 text-sm font-medium text-gray-700 underline underline-offset-4 hover:text-black">
              Voir toutes mes expéditions <FaArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
          <a
            href={`https://wa.me/22945387945?text=${encodeURIComponent(`Bonjour, j'ai besoin d'aide concernant ma commande ${confirmedTrackingId}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 text-sm font-medium text-green-600 hover:text-green-700"
          >
            <FaWhatsapp className="w-4 h-4" /> Besoin d'aide ? Écrivez au support (+229 45387945)
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Lot 3 — modale OTP */}
      {otpOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <h3 className="text-xl font-bold text-gray-900">Vérification du numéro</h3>
            <p className="text-sm text-gray-600">
              Nous avons envoyé un code à 4 chiffres par WhatsApp au <span className="font-medium">{otpPhone}</span>.
              Saisissez-le pour confirmer votre commande.
            </p>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={4}
              value={otpCode}
              onChange={(e) => { setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 4)); setOtpError(''); }}
              placeholder="1234"
              className="w-full text-center tracking-[0.5em] text-2xl font-bold bg-gray-100 rounded-lg py-3 focus:outline-none focus:ring-2 focus:ring-black"
            />
            {otpError && <p className="text-sm text-red-600">{otpError}</p>}
            <button
              type="button"
              onClick={verifyOtp}
              disabled={otpVerifying}
              className="w-full px-6 py-3 rounded-lg font-medium bg-black text-white hover:bg-gray-800 disabled:bg-gray-300 flex items-center justify-center gap-2"
            >
              {otpVerifying ? <Spinner /> : 'Vérifier et commander'}
            </button>
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={resendOtp}
                disabled={otpSending}
                className="text-gray-600 underline underline-offset-4 disabled:text-gray-300"
              >
                {otpSending ? 'Envoi…' : 'Renvoyer le code'}
              </button>
              <a
                href="https://wa.me/22945387945?text=Bonjour%2C%20je%20n'ai%20pas%20re%C3%A7u%20mon%20code%20de%20v%C3%A9rification%20OuiGoo."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-green-600 hover:text-green-700"
              >
                <FaWhatsapp className="w-4 h-4" /> Pas reçu le code ?
              </a>
            </div>
            <button type="button" onClick={() => setOtpOpen(false)} className="w-full text-xs text-gray-400 hover:text-gray-600">
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12">

          {/* Left Column - Form */}
          <div className="w-full lg:w-1/2 space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                Commandez une livraison en quelques clics
              </h1>
              <p className="text-gray-600 text-lg">
                Sans créer de compte : renseignez le point de départ et le point d'arrivée, validez, et suivez votre colis en temps réel.
              </p>
            </div>

            <form onSubmit={handleValidate} className="space-y-5 max-w-md">
              {(
                <>
                  {/* Lot 6 — Qui commande ? */}
                  {isQuoteLot6On() && (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-gray-700">Qui commande ?</p>
                      <div className="grid grid-cols-3 gap-1 p-1 bg-gray-100 rounded-lg">
                        <button
                          type="button"
                          onClick={() => setSenderType('sender')}
                          className={`px-2 py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${senderType === 'sender' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                        >
                          J'envoie
                        </button>
                        <button
                          type="button"
                          onClick={() => setSenderType('buyer')}
                          className={`px-2 py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${senderType === 'buyer' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                        >
                          J'achète
                        </button>
                        <button
                          type="button"
                          onClick={() => setSenderType('merchant')}
                          className={`px-2 py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${senderType === 'merchant' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                        >
                          Je suis vendeur
                        </button>
                      </div>
                      {senderType === 'merchant' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                          <div>
                            <div className="relative">
                              <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                              <input
                                type="text"
                                value={merchant.name}
                                onChange={(e) => { setMerchant((m) => ({ ...m, name: e.target.value })); clearFieldError('merchantName'); }}
                                placeholder="Votre nom (vendeur)"
                                className={textInputClassName}
                              />
                            </div>
                            {fieldErrors.merchantName && <p className="mt-1 text-sm text-red-600">{fieldErrors.merchantName}</p>}
                          </div>
                          <div>
                            <div className="relative">
                              <FaPhoneAlt className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                              <span className="absolute left-11 top-1/2 -translate-y-1/2 text-sm text-gray-500 font-medium pointer-events-none">{phonePrefix}</span>
                              <input
                                type="tel"
                                value={merchant.phone}
                                onChange={(e) => { setMerchant((m) => ({ ...m, phone: sanitizePhone(e.target.value) })); clearFieldError('merchantPhone'); }}
                                placeholder="Votre téléphone"
                                className={`${textInputClassName} !pl-[5.25rem]`}
                              />
                            </div>
                            {fieldErrors.merchantPhone && <p className="mt-1 text-sm text-red-600">{fieldErrors.merchantPhone}</p>}
                          </div>
                        </div>
                      )}
                      <p className="text-xs text-gray-500">
                        {senderType === 'buyer'
                          ? "Vous achetez et vous faites livrer : le code de vérification ira sur votre numéro de livraison (le vendeur est à la récupération)."
                          : senderType === 'merchant'
                          ? "Indiquez votre propre numéro (différent du fournisseur et du client) pour retrouver toutes vos expéditions."
                          : "Vous envoyez un colis : le code de vérification ira sur votre numéro de récupération."}
                      </p>
                    </div>
                  )}

                  {/* Pickup */}
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-700">Point de récupération</p>
                    {renderCitySelect(pickup, setPickup, 'pickupCity', 'Ville de récupération')}
                    <PlaceAutocompleteInput
                      value={pickup.value}
                      onChange={(v) => updatePoint(setPickup, { value: v })}
                      onPlaceSelect={(place) => { updatePoint(setPickup, { place, value: place.name || place.address || pickup.value }, ['pickupPlace']); trackStep('pickup_selected'); }}
                      placeholder="Lieu de ramassage"
                      country={country}
                      icon={<div className="w-2.5 h-2.5 rounded-full bg-black"></div>}
                      inputClassName={inputClassName}
                      disabled={inputsDisabled}
                    />
                    {fieldErrors.pickupPlace && (
                      <p className="text-sm text-red-600">{fieldErrors.pickupPlace}</p>
                    )}
                    {renderContactFields(pickup, setPickup, { name: 'pickupName', phone: 'pickupPhone' }, "Nom de l'expéditeur")}
                  </div>

                  {/* Dropoff */}
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-700">Point d'arrivée</p>
                    {renderCitySelect(dropoff, setDropoff, 'dropoffCity', 'Ville de livraison')}
                    <PlaceAutocompleteInput
                      value={dropoff.value}
                      onChange={(v) => updatePoint(setDropoff, { value: v })}
                      onPlaceSelect={(place) => { updatePoint(setDropoff, { place, value: place.name || place.address || dropoff.value }, ['dropoffPlace']); trackStep('delivery_selected'); }}
                      placeholder="Lieu de livraison"
                      country={country}
                      icon={<div className="w-2.5 h-2.5 bg-black"></div>}
                      inputClassName={inputClassName}
                      disabled={inputsDisabled}
                    />
                    {fieldErrors.dropoffPlace && (
                      <p className="text-sm text-red-600">{fieldErrors.dropoffPlace}</p>
                    )}
                    {renderContactFields(dropoff, setDropoff, { name: 'dropoffName', phone: 'dropoffPhone' }, 'Nom du destinataire')}
                  </div>
                </>
              )}

              {/* Action Buttons (desktop keeps them here; on mobile after a result they
                  move to the bottom, under the map + details) */}
              {deliveryType === 'Local' && (
              <div className={`pt-2 items-center gap-4 ${simulationResult ? 'hidden lg:flex' : 'flex'}`}>
                {!simulationResult ? (
                  <button
                    type="submit"
                    disabled={loading}
                    className={`px-8 py-3.5 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      loading
                        ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                        : 'bg-black text-white hover:bg-gray-800'
                    }`}
                  >
                    {loading ? <Spinner /> : (<>Valider <FaArrowRight className="w-4 h-4" /></>)}
                  </button>
                ) : outOfCoverage ? (
                  <div className="px-4 py-3 rounded-lg bg-red-50 text-red-700 text-sm font-medium border border-red-100">
                    Désolé, cette zone n'est pas encore couverte par OuiGoo.
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={confirming}
                    className={`px-8 py-3.5 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      confirming
                        ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                        : 'bg-black text-white hover:bg-gray-800'
                    }`}
                  >
                    {confirming ? <Spinner /> : (<>Confirmer la course <FaArrowRight className="w-4 h-4" /></>)}
                  </button>
                )}
              </div>
              )}

              {/* Lot 2 — repli : impossible de placer les points */}
              {isQuoteLot2On() && deliveryType === 'Local' && !simulationResult && (
                <button
                  type="button"
                  onClick={handleFallback}
                  className="text-sm text-gray-500 underline underline-offset-4 hover:text-gray-700"
                >
                  Je n'arrive pas à placer mes points : être rappelé
                </button>
              )}
            </form>

            {/* Errors */}
            {error && (
              <div className="mt-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-100">
                {error}
              </div>
            )}
          </div>

          {/* Right Column - Map or Illustration */}
          <div className="w-full lg:w-1/2 relative">
            {showMap && simulationResult && pickup.place ? (
              /* Google Maps with delivery route */
              <div className="aspect-square rounded-3xl overflow-hidden bg-gray-100 relative">
                <DeliveryMap
                  simulationResult={simulationResult}
                  pickupPlace={pickup.place}
                  dropoffs={[dropoff]}
                  pickupName={pickup.value}
                />

                {/* Estimation Overlay (desktop) — masquée hors zone de couverture */}
                <div className="absolute left-4 right-4 bottom-4 bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100 shadow-lg p-4 space-y-3 hidden lg:block">
                  {outOfCoverage ? (
                    <p className="text-center text-sm font-semibold text-red-600">Zone pas encore couverte par OuiGoo.</p>
                  ) : (
                  <div className="space-y-3">
                    {renderPriceControl(true)}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                        <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Distance totale</p>
                        <p className="text-lg font-bold text-gray-900">
                          {simulationResult.grandTotalDistance ? formatDistance(simulationResult.grandTotalDistance) : '-'}
                        </p>
                      </div>
                      <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                        <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Durée totale</p>
                        <p className="text-lg font-bold text-gray-900">
                          {simulationResult.grandTotalDuration ? formatDuration(simulationResult.grandTotalDuration) : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              </div>
            ) : (
              /* Default illustration when no validation yet */
              <div className="aspect-square rounded-3xl overflow-hidden bg-gray-100 relative">
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-tr from-orange-100 via-orange-50 to-blue-50">
                  <div className="text-4xl text-orange-500 font-bold opacity-20">OuiGoo</div>
                </div>
              </div>
            )}
          </div>

          {/* Mobile only: details under the map, then the action at the very bottom */}
          {showMap && simulationResult && (
            <div className="w-full space-y-4 lg:hidden">
              {!outOfCoverage && (
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-3">
                <h3 className="text-lg font-semibold">Estimation</h3>
                {renderPriceControl(false)}
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Distance totale</p>
                    <p className="text-base font-bold text-gray-900">
                      {simulationResult.grandTotalDistance ? formatDistance(simulationResult.grandTotalDistance) : '-'}
                    </p>
                  </div>
                  <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Durée totale</p>
                    <p className="text-base font-bold text-gray-900">
                      {simulationResult.grandTotalDuration ? formatDuration(simulationResult.grandTotalDuration) : '-'}
                    </p>
                  </div>
                </div>
              </div>
              )}
              <div ref={mobileResultRef} className="scroll-mb-6">
                {outOfCoverage ? (
                  <div className="w-full px-4 py-3 rounded-lg bg-red-50 text-red-700 text-sm font-medium border border-red-100 text-center">
                    Désolé, cette zone n'est pas encore couverte par OuiGoo.
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={confirming}
                    className={`w-full px-8 py-3.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                      confirming ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-black text-white hover:bg-gray-800'
                    }`}
                  >
                    {confirming ? <Spinner /> : (<>Confirmer la commande <FaArrowRight className="w-4 h-4" /></>)}
                  </button>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// Render function for Wrapper
const renderStatus = (status) => {
  if (status === Status.LOADING) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }
  if (status === Status.FAILURE) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-red-500">Erreur de chargement de Google Maps</p>
      </div>
    );
  }
  return null;
};

// Main QuickDelivery component with Google Maps Wrapper
const QuickDelivery = () => {
  return (
    <Wrapper
      apiKey={GOOGLE_MAPS_API_KEY}
      libraries={['places', 'marker']}
      render={renderStatus}
    >
      <QuickDeliveryContent />
    </Wrapper>
  );
};

export default QuickDelivery;
